// «Что работает» (этап 6, PLAN §7): свойства роликов × результаты в рекламе. Цифры считает платформа:
// находки (где апрув заметно дешевле или дороже среднего), сводка по выбранному свойству, ролики по расходу.
// Выводы о хуках и углах — у агента: кнопка отправляет ему вопрос с контекстом страницы.
// Период — общий выбор (по умолчанию 30 дней); таблицы сортируются по колонке.
import { Button, Card, EmptyState, int, Segmented, Skeleton, usd } from '@autoaff/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Lightbulb, MessageCircle, Sparkles } from 'lucide-react';
import { useMemo, useState } from 'react';
import { api } from '../../api';
import { chatStore } from '../../chat/store';
import {
  compareBy,
  FilterBar,
  FilterSelect,
  PeriodPicker,
  periodKey,
  periodLabel,
  periodPhrase,
  SortTh,
  usePeriod,
  useSort,
} from '../../filters';
import { PageHead } from '../common';
import { CreativeTabs } from './CreativeTabs';
import './creatives.css';

type Dim = 'delivery' | 'gender' | 'voice' | 'music' | 'duration' | 'gen' | 'lang' | 'studio' | 'offer';

interface Metrics {
  spend: number;
  impressions: number;
  leads: number;
  approved: number;
  inProcess: number;
  approveRate: number | null;
  approveCostForecast: number | null;
  approvesForecast: number;
  forecastProfit: number;
  hookRate: number | null;
}

interface Group extends Metrics {
  value: string;
  creatives: number;
  thin: boolean;
  delta: number | null;
}

interface WorksView {
  since: string;
  until: string;
  total: Metrics & { creatives: number };
  findings: Array<{ dim: Dim; value: string; delta: number; text: string }>;
  groups: Record<Dim, Group[]>;
  creatives: Array<
    Metrics & {
      id: number;
      code: string;
      offerName: string;
      hook: string | null;
      attrs: Record<Dim, string | null>;
    }
  >;
  offers: Array<{ id: number; name: string }>;
}

const DIMS: Array<{ value: Dim; label: string }> = [
  { value: 'delivery', label: 'Подача' },
  { value: 'gender', label: 'Голос' },
  { value: 'voice', label: 'Диктор' },
  { value: 'music', label: 'Музыка' },
  { value: 'duration', label: 'Длина' },
  { value: 'gen', label: 'Генерация' },
  { value: 'lang', label: 'Язык' },
  { value: 'studio', label: 'Студия' },
  { value: 'offer', label: 'Оффер' },
];

type GroupKey =
  | 'value'
  | 'creatives'
  | 'spend'
  | 'leads'
  | 'approveRate'
  | 'approveCostForecast'
  | 'hookRate';
type CreoKey = 'code' | 'spend' | 'leads' | 'approveCostForecast' | 'hookRate' | 'forecastProfit';

const money = (n: number | null) => (n === null ? '—' : usd(n));
const pct1 = (n: number | null) => (n === null ? '—' : `${n.toFixed(1)}%`);

/** Отличие цены апрува от средней: дешевле — зелёный (деньги), дороже — красный. */
function Delta({ d, thin }: { d: number | null; thin: boolean }) {
  if (d === null || Math.abs(d) < 0.05) return null;
  const cls = thin ? 'muted' : d < 0 ? 'pos' : 'neg';
  return (
    <span className={`wk-delta ${cls}`}>
      {d < 0 ? '−' : '+'}
      {Math.round(Math.abs(d) * 100)}%
    </span>
  );
}

export function WorksPage() {
  const [period, setPeriod] = usePeriod('works', '30d');
  const [offer, setOffer] = useState<string>('');
  const [dim, setDim] = useState<Dim>('delivery');
  const [groupSort, sortGroups] = useSort<GroupKey>({ key: 'spend', dir: 'desc' });
  const [creoSort, sortCreos] = useSort<CreoKey>({ key: 'spend', dir: 'desc' });
  const key = periodKey(period);
  const q = useQuery({
    queryKey: ['works', key, offer],
    queryFn: () => api<WorksView>(`/api/creatives/works?period=${key}${offer ? `&offer=${offer}` : ''}`),
    placeholderData: keepPreviousData,
  });
  const d = q.data;
  const offerName = d?.offers.find((o) => String(o.id) === offer)?.name;
  const phrase = periodPhrase(period);
  const groups = useMemo(
    () => [...(d?.groups[dim] ?? [])].sort(compareBy<Group>((g) => g[groupSort.key], groupSort.dir)),
    [d, dim, groupSort],
  );
  const creos = useMemo(
    () =>
      [...(d?.creatives ?? [])]
        .sort(compareBy<WorksView['creatives'][number]>((c) => c[creoSort.key], creoSort.dir))
        .slice(0, 50),
    [d, creoSort],
  );

  const ask = () => {
    chatStore.setThread(null);
    chatStore.setContext({
      kind: 'page',
      label: `Креативы → Что работает · ${periodLabel(period)} · ${offerName ?? 'все офферы'}`,
    });
    chatStore.open({
      draft: `Посмотри «Что работает» ${phrase}${d ? ` (${d.since} … ${d.until})` : ''}${offerName ? ` по офферу «${offerName}»` : ''} (what_works). Что из этого следует для следующего батча: какие хуки, подачу и голос брать, от чего отказаться? Отдели выводы от догадок — где данных мало, скажи прямо.`,
    });
  };

  return (
    <>
      <PageHead
        title="Креативы"
        sub="Что работает: свойства роликов против результатов в рекламе"
        actions={
          <Button onClick={ask} disabled={!d || d.total.creatives === 0}>
            <MessageCircle />
            Спросить агента
          </Button>
        }
      />
      <CreativeTabs active="/creatives/works" />
      <div className="pc">
        <FilterBar>
          <PeriodPicker value={period} onChange={setPeriod} />
          {d && (d.offers.length > 1 || offer) && (
            <FilterSelect
              label="Оффер"
              value={offer}
              onChange={setOffer}
              allValue=""
              options={[
                { value: '', label: 'Все' },
                ...d.offers.map((o) => ({ value: String(o.id), label: o.name })),
              ]}
            />
          )}
        </FilterBar>

        {q.isPending && (
          <div className="flex flex-col gap-3">
            <Skeleton h={90} />
            <Skeleton h={220} />
          </div>
        )}
        {q.isError && (
          <div className="card">
            <EmptyState
              icon={<Sparkles />}
              title="Не удалось посчитать"
              action={<Button onClick={() => q.refetch()}>Повторить</Button>}
            >
              {(q.error as Error).message}
            </EmptyState>
          </div>
        )}
        {d && d.total.creatives === 0 && (
          <div className="card">
            <EmptyState
              icon={<Sparkles />}
              title="Пока не с чем сравнивать"
              action={
                <Link to="/ads" className="btn">
                  Открыть рекламу
                </Link>
              }
            >
              Нет роликов с расходом или лидами {phrase}. Сравнение появится, когда видео объявлений будут
              связаны с роликами пула и по ним пойдут лиды.
            </EmptyState>
          </div>
        )}
        {d && d.total.creatives > 0 && (
          <>
            <section className="kpis" aria-label={`Итоги роликов ${phrase}`}>
              <div className="kpi">
                <span>Роликов в рекламе</span>
                <b>{d.total.creatives}</b>
              </div>
              <div className="kpi">
                <span>Расход</span>
                <b>{usd(d.total.spend)}</b>
              </div>
              <div className="kpi">
                <span>Лидов</span>
                <b>{int(d.total.leads)}</b>
                <small>апрувов {d.total.approved}</small>
              </div>
              <div className="kpi">
                <span>Цена апрува</span>
                <b>{money(d.total.approveCostForecast)}</b>
                <small>прогноз с лидами в обработке</small>
              </div>
            </section>

            <Card title="Находки">
              {d.findings.length ? (
                <ul className="wk-findings">
                  {d.findings.map((f) => (
                    <li key={`${f.dim}:${f.value}`} data-tone={f.delta < 0 ? 'pos' : 'neg'}>
                      <Lightbulb aria-hidden="true" />
                      <span>{f.text}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted wk-note">
                  Заметных различий нет: либо данных пока мало (меньше 15 лидов в группе), либо свойства дают
                  близкую цену апрува.
                </p>
              )}
              <p className="muted wk-note">
                Сравниваем прогнозную цену апрува: апрувы плюс лиды в обработке по текущему % апрува. Свойства
                пересекаются с оффером — внутри одного оффера сравнение точнее.
              </p>
            </Card>

            <Card title="По свойству">
              <div className="wk-dims">
                <Segmented label="Свойство" value={dim} onChange={setDim} options={DIMS} />
              </div>
              <div className="fin-tbl-wrap">
                <table className="fin-tbl wk-tbl">
                  <thead>
                    <tr>
                      <SortTh k="value" sort={groupSort} onSort={sortGroups} firstDir="asc">
                        {DIMS.find((x) => x.value === dim)?.label}
                      </SortTh>
                      <SortTh k="creatives" sort={groupSort} onSort={sortGroups} className="num hide-m">
                        Роликов
                      </SortTh>
                      <SortTh k="spend" sort={groupSort} onSort={sortGroups} className="num">
                        Расход
                      </SortTh>
                      <SortTh k="leads" sort={groupSort} onSort={sortGroups} className="num">
                        Лиды
                      </SortTh>
                      <SortTh k="approveRate" sort={groupSort} onSort={sortGroups} className="num hide-m">
                        Апрув
                      </SortTh>
                      <SortTh
                        k="approveCostForecast"
                        sort={groupSort}
                        onSort={sortGroups}
                        firstDir="asc"
                        className="num"
                      >
                        Цена апрува
                      </SortTh>
                      <SortTh k="hookRate" sort={groupSort} onSort={sortGroups} className="num hide-m">
                        Досмотр 3 с
                      </SortTh>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((g) => (
                      <tr key={g.value} className={g.thin ? 'thin' : undefined}>
                        <td>
                          {g.value}
                          {g.thin && <span className="sub">мало данных</span>}
                        </td>
                        <td className="num hide-m">{g.creatives}</td>
                        <td className="num">{usd(g.spend)}</td>
                        <td className="num">{int(g.leads)}</td>
                        <td className="num hide-m">{pct1(g.approveRate)}</td>
                        <td className="num">
                          {money(g.approveCostForecast)} <Delta d={g.delta} thin={g.thin} />
                        </td>
                        <td className="num hide-m">{pct1(g.hookRate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            <Card title="Ролики" count={d.creatives.length}>
              <div className="fin-tbl-wrap">
                <table className="fin-tbl wk-tbl">
                  <thead>
                    <tr>
                      <SortTh k="code" sort={creoSort} onSort={sortCreos} firstDir="asc">
                        Ролик
                      </SortTh>
                      <th className="hide-m">Хук</th>
                      <SortTh k="spend" sort={creoSort} onSort={sortCreos} className="num">
                        Расход
                      </SortTh>
                      <SortTh k="leads" sort={creoSort} onSort={sortCreos} className="num">
                        Лиды
                      </SortTh>
                      <SortTh
                        k="approveCostForecast"
                        sort={creoSort}
                        onSort={sortCreos}
                        firstDir="asc"
                        className="num"
                      >
                        Цена апрува
                      </SortTh>
                      <SortTh k="hookRate" sort={creoSort} onSort={sortCreos} className="num hide-m">
                        Досмотр 3 с
                      </SortTh>
                      <SortTh k="forecastProfit" sort={creoSort} onSort={sortCreos} className="num hide-m">
                        Прибыль, прогноз
                      </SortTh>
                    </tr>
                  </thead>
                  <tbody>
                    {creos.map((c) => (
                      <tr key={c.id}>
                        <td>
                          <Link to="/creatives/$id" params={{ id: String(c.id) }}>
                            {c.code}
                          </Link>
                          <span className="sub">
                            {[c.attrs.delivery, c.attrs.gender, offer ? null : c.offerName]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        </td>
                        <td className="hide-m wk-hook">{c.hook ?? '—'}</td>
                        <td className="num">{usd(c.spend)}</td>
                        <td className="num">{int(c.leads)}</td>
                        <td className="num">{money(c.approveCostForecast)}</td>
                        <td className="num hide-m">{pct1(c.hookRate)}</td>
                        <td className={`num hide-m ${c.forecastProfit >= 0 ? 'pos' : 'neg'}`}>
                          {usd(c.forecastProfit)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}
      </div>
    </>
  );
}
